import React, { useContext, createContext, useEffect, useState, useMemo } from "react"
import { ApolloClient, ApolloProvider, InMemoryCache, createHttpLink, useLazyQuery, useMutation, gql } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { UserContext } from '../utils/usermanagement'

export const ParentAPIContext = createContext(null);

// Apollo Client Configuration
const httpLink = createHttpLink({
  uri: process.env.GATSBY_APP_SYNC_ENDPOINT,
});

const authLink = setContext((_, { headers }) => {
  // get the authentication token from local storage if it exists
  const token = localStorage.getItem('cognito_id_token');
  // return the headers to the context so httpLink can read them
  return {
    headers: {
      ...headers,
      authorization: token ? token : "",
    }
  }
});

const client = new ApolloClient({
  link: authLink.concat(httpLink),
  cache: new InMemoryCache()
});

// GraphQL Query

const getChildrenQuery = gql`
  query getChildrenQuery {
    allChildren {
      child
      nickname
	  avatar
	  parent
      licence_issued
      badges
      current_module
      current_module_start
      progress
    }
  }
`;

const addChildMutation = gql`
	mutation AddChild($nickname: String!, $avatar: String!, $password: String!) {
		addChild(nickname: $nickname, avatar: $avatar, password: $password) {
			nickname
			avatar
			child
			parent
		}
}`

const deleteChildMutation = gql`
	mutation deleteChild($childId: ID!) {
		deleteChild(child: $childId) {
			child
			parent
	}
}`

const updateChildMutation = gql`
	mutation UpdateChild($childId: ID!, $nickname: String!, $avatar: String!) {
		updateChild(child: $childId , nickname: $nickname, avatar:$avatar) {
			child
			nickname
			avatar
			parent

	}
}`


const updateChildPasswordMutation = gql`
	mutation setChildPassword ($childId: ID!, $password: String!){
		setChildPassword(child: $childId, password: $password)
}`


const insertChildInPrizeDrawMutation = gql`
	mutation setPrizeDraw($child: ID!, $first_name: String!, $last_name: String!, $email: String!, $telephone: String, $consent: Boolean!) {
		setPrizeDraw(child: $child, first_name: $first_name, last_name: $last_name, email: $email, telephone: $telephone, consent: $consent) {
			nickname
			avatar
			child
			parent
		}
}`

const updateLoginTimeMutation = gql`
  mutation updateLoginTime {
		updateLoginTime
	}
`
// Main component

const ParentAPIComponent = ({ children }) => {
	const [userState, userDispatch] = useContext(UserContext);
	const [parentAPIState, setParentAPIState] = useState({action:"first-renderd"});
	const [childrenList, setChildrenList] = useState({});

	const parentAPI = useMemo(() => [parentAPIState, setParentAPIState], [parentAPIState]);
	const parentChildren = useMemo(() => [childrenList, setChildrenList], [childrenList]);


	const [getChildren, { data }] = useLazyQuery(getChildrenQuery, {
		fetchPolicy: 'no-cache',
		onError(error) {
			if(error?.networkError?.statusCode === 401) {
				console.error("Logginig out due to 401 from AppSync ...")
				userDispatch({ action: "logout" })
			}
		}
	});

	const [addChild] = useMutation(addChildMutation, {
		onError(error) {
		  	setParentAPIState({action: 'add-child-error', error});
		},
		onCompleted(data) {
			setParentAPIState({action: 'add-child-success', data});
		}
	});

	const [deleteChild] = useMutation(deleteChildMutation, {
		onError(error) {
		  	setParentAPIState({action: 'delete-child-error', error});
		},
		onCompleted(data) {
			setParentAPIState({action: 'delete-child-success', data});
		}
	});

	const [updateChild] = useMutation(updateChildMutation, {
		onError(error) {
		  	setParentAPIState({action: 'update-child-error', error});
		},
		onCompleted(data) {
			setParentAPIState({action: 'update-child-success', data});
		}
	});

	const [updateChildPassword] = useMutation(updateChildPasswordMutation, {
		onError(error) {
		  	setParentAPIState({action: 'update-child-password-error', error});
		},
		onCompleted(data) {
			setParentAPIState({action: 'update-child-password-success', data});
		}
  });

  const [insertChildInPrizeDraw] = useMutation(insertChildInPrizeDrawMutation, {
    onError(error) {
		  setParentAPIState({action: 'insert-prizedraw-error', error, errorMsg: error.message});
		},
		onCompleted(data) {
			setParentAPIState({action: 'insert-prizedraw-success', data});
		}
	});

  const [updateLoginTime] = useMutation(updateLoginTimeMutation, {
    onError(error) {
		  setParentAPIState({action: 'update-login-time-error', error, errorMsg: error.message});
		},
		onCompleted(data) {
			setParentAPIState({action: 'update-login-time-success', data});
		}
	});

	useEffect(() => {
		if(userState.cognito_user && parentAPIState) {
			if(parentAPIState.action === 'add-child'){
				var addData = { nickname: parentAPIState.data.nickname, avatar: parentAPIState.data.avatar, password: parentAPIState.data.password };
				addChild({ variables: addData });
			}

			if(parentAPIState.action === 'delete-child'){
				var deleteData = { childId: parentAPIState.data.childId};
				deleteChild({ variables: deleteData });
			}

			if(parentAPIState.action === 'update-child'){
				var updateData = { childId: parentAPIState.data.childId, nickname: parentAPIState.data.nickname, avatar: parentAPIState.data.avatar};
				updateChild({ variables: updateData });
			}

			if(parentAPIState.action === 'update-child-password'){
				var updatePasswordData = { childId: parentAPIState.data.childId, password: parentAPIState.data.password};
				updateChildPassword({ variables: updatePasswordData });
			}

      if(parentAPIState.action === 'insert-child-prizedraw'){
				var insertData = { child: parentAPIState.data.child, first_name: parentAPIState.data.first_name, last_name: parentAPIState.data.last_name,
          email: parentAPIState.data.email, telephone: parentAPIState.data.telephone, consent: parentAPIState.data.consent };
				insertChildInPrizeDraw({ variables: insertData });
			}

			if(parentAPIState.action === 'update-login-time') {
				updateLoginTime();
			}
		}

	}, [userState.cognito_user, parentAPIState, addChild, deleteChild, updateChild, updateChildPassword, insertChildInPrizeDraw])

	useEffect(() => {
		if(!data?.allChildren) {
			setChildrenList({ allChildren: [] })
		} else {

			setChildrenList({
				allChildren: data.allChildren.map(child => {
					let progress = { current_episode: 0, current_chapter: 1 }
					try {
						progress=JSON.parse(child.progress)
					}
					catch(e) {
						// continue with existing blank progress
					}
					return({...child, progress: progress})
				})
			})
		}
	}, [data])

	useEffect(() => {
		if(userState.cognito_user && parentAPIState) {
			if(	parentAPIState.action === 'first-renderd' ||
				parentAPIState.action === 'add-child-success' ||
				parentAPIState.action === 'delete-child-success' ||
				parentAPIState.action === 'update-child-success' ||
				parentAPIState.action === 'update-child-password-success' ) {
					getChildren();
			}
		}
	}, [userState.cognito_user, parentAPIState, getChildren])


  return(
    <ParentAPIContext.Provider value={{ parentAPI: parentAPI, parentChildren: parentChildren }}>
      {children}
    </ParentAPIContext.Provider>
  )
}

// Wrapper component

const ParentAPI = ({ children }) => {
  return(
    <ApolloProvider client={client}>
      <ParentAPIComponent>
        {children}
      </ParentAPIComponent>
    </ApolloProvider>
  )
}

export default ParentAPI
